"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GamingSecurityHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stats = [{
    label: "UPTIME",
    value: "99.9%"
  }, {
    label: "LATENCY",
    value: "12 MS"
  }, {
    label: "SERVERS",
    value: "500+"
  }];
  return <section data-section-id="2562" id="gamingsecurityhero" className="bg-background text-foreground relative overflow-hidden min-h-[90vh] flex items-center py-20 lg:py-0">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-30" style={{
      background: "radial-gradient(ellipse at 70% 50%, hsl(22 100% 50% / 0.15) 0%, transparent 65%)"
    }} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-6 lg:pt-20 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Security
              </span>
              <span className="font-mono text-xs text-muted-foreground">—</span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-foreground">
                Gaming Protection
              </span>
            </div>

            {}
            <h1 className="font-serif font-light text-foreground leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-[5.5rem]">
              Gaming,{" "}
              <span className="italic font-normal">shielded</span>
              <br />
              with total
              <br />
              dominance.
            </h1>

            {}
            <p className="mt-10 max-w-[400px] text-base leading-relaxed text-muted-foreground font-default">
              Our elite VPN infrastructure delivers ultra-low latency, DDoS protection, 
              and global server access — ensuring your competitive edge remains sharp 
              and your connection stays unbreakable.
            </p>

            {}
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6">
              <Button asChild size="lg" className="h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-[0.18em] rounded-md">
                <Link to="https://vpngenix.com/go/NordVPN" newTab>Secure your game</Link>
              </Button>

              <Link to="/GamingVPNsPage" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-foreground pb-1 border-b border-foreground/60 hover:border-foreground transition-colors w-fit">
                <span>View gaming plans</span>
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 relative transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_setup.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_setup.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/gaming_vpn_setup.png-wvc-sizes" alt="High-performance gaming setup with secure VPN connection active" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

              {}
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(180deg, transparent 40%, hsl(221 40% 4% / 0.4) 100%)"
            }} aria-hidden="true" />

              {}
              <div className="absolute left-4 right-4 sm:left-6 sm:right-auto bottom-6 sm:max-w-[280px] bg-card/95 backdrop-blur-sm border border-border rounded-md p-5 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Performance
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    / 03
                  </span>
                </div>
                <div className="space-y-0">
                  {stats.map((stat, i) => <div key={i} data-index={i}>
                      <div className="flex items-center justify-between py-2.5">
                        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                          {stat.label}
                        </span>
                        <span className="font-mono text-sm text-foreground tabular-nums">
                          {stat.value}
                        </span>
                      </div>
                      {i < stats.length - 1 && <Separator className="bg-border/60" />}
                    </div>)}
                </div>
              </div>
            </div>

            {}
            <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span>FIG. 01</span>
              <span>GAMING NODE / VPNGENIX</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}